import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {FileSelectEvent} from 'primeng/fileupload';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {QlService} from '../../../../../core/shared/ql/service/ql.service';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {HgPartnerService} from '../../../service/api/hg-partner.service';
import {HgPartnerDto} from '../../../model/dto/entity/hg-partner.dto';

/**
 * Combined "Schema einrichten" dialog for an HgPartner.
 *
 * Wraps the two backend endpoints exposed by {@link HgPartnerService}:
 *  - {@link HgPartnerService.ensureSchema} when no extension XML file is supplied
 *    (idempotent — creates the merged GDV standard schema if missing).
 *  - {@link HgPartnerService.uploadExtensionSchema} when the user uploads an XML
 *    extension file (REPLACES the partner's current schema).
 *
 * The dialog asks for confirmation when an upload would overwrite an existing
 * schema.
 *
 * @ticket TM-1000001721
 */
@Component({
    selector: 'hg-partner-schema-setup-dialog',
    templateUrl: './hg-partner-schema-setup-dialog.component.html',
    standalone: false
})
export class HgPartnerSchemaSetupDialogComponent implements OnInit, OnChanges, OnDestroy {

    /** Two-way binding for dialog visibility. */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** The HgPartner the schema is being set up for. */
    @Input() partner: HgPartnerDto;

    /** Emitted after a successful ensure/upload call so the parent can reload. */
    @Output() onSchemaUpdated = new EventEmitter<HgPartnerDto>();

    private readonly destroy$ = new Subject<void>();

    /** Selected EmSchemaType id (required). */
    schemaTypeId: number;

    /** Options for the EmSchemaType p-select. */
    schemaTypeOptions: { label: string; value: number }[] = [];
    schemaTypesLoading: boolean = false;

    /** Whether HANDBOOK / FAQ ROOT structures should be emitted. */
    includeNonDataMessageTypes: boolean = false;

    /** Optional extension schema XML file. Empty = ensureSchema, set = uploadExtensionSchema. */
    selectedFile: File;

    /** Optional organization name; pre-filled with partner.name on dialog open. */
    organizationName: string;

    /** True while an HTTP call is in flight. */
    busy: boolean = false;

    constructor(
        protected partnerService: HgPartnerService,
        protected qlService: QlService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService) {
    }

    ngOnInit(): void {
        this.loadSchemaTypes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // When the dialog is (re-)opened, reset transient state and prefill the
        // organization name from the partner.
        if (changes['visible']) {
            const becameVisible = changes['visible'].currentValue === true;
            const wasVisible = changes['visible'].previousValue === true;
            if (becameVisible && !wasVisible) {
                this.resetForm();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Loads selectable EmSchemaType entries via QL for the schemaTypeId picker.
     *
     * @ticket TM-1000001721
     */
    loadSchemaTypes(): void {
        this.schemaTypesLoading = true;
        this.qlService.builder()
            .query({
                name: 'emSchemaTypes',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'em.EmSchemaType',
                    as: 'e'
                },
                sortings: [new Sorting('e.name', true)]
            })
            .pipeType('simple')
            .execute()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    const entries: any[] = result.get('emSchemaTypes') || [];
                    this.schemaTypeOptions = entries.map(e => ({
                        label: e['e.name'],
                        value: e['e.id']
                    }));
                    this.schemaTypesLoading = false;
                },
                error: () => {
                    this.schemaTypesLoading = false;
                    this.messageService.showErrorMessage(
                        'Schema-Typen',
                        'EmSchemaTypen konnten nicht geladen werden.'
                    );
                }
            });
    }

    /**
     * Resets the transient form fields when the dialog is opened.
     *
     * @ticket TM-1000001721
     */
    private resetForm(): void {
        this.schemaTypeId = null;
        this.includeNonDataMessageTypes = false;
        this.selectedFile = null;
        this.organizationName = this.partner?.name || '';
        this.busy = false;
    }

    /**
     * Handles file selection from the p-fileupload component.
     *
     * @ticket TM-1000001721
     */
    onFileSelect(event: FileSelectEvent): void {
        if (event.files?.length) {
            this.selectedFile = event.files[0];
        }
    }

    /**
     * Clears the currently selected extension schema file.
     *
     * @ticket TM-1000001721
     */
    clearFile(): void {
        this.selectedFile = null;
    }

    /**
     * Submit handler. Routes to ensureSchema or uploadExtensionSchema based on
     * whether a file is supplied, with a confirmation step when the upload
     * would replace an existing schema.
     *
     * @ticket TM-1000001721
     */
    submit(): void {
        if (this.busy || !this.partner?.id || !this.schemaTypeId) {
            return;
        }

        const hasExistingSchema = !!this.partner.schemaDtoId;
        const hasFile = !!this.selectedFile;

        if (hasExistingSchema && hasFile) {
            this.confirmationService.confirm({
                header: 'Bestehendes Schema ersetzen?',
                message: `Der Partner hat bereits ein Schema (ID: ${this.partner.schemaDtoId}). `
                    + 'Beim Hochladen eines Erweiterungsschemas wird das aktuelle Schema vollständig ersetzt. Fortfahren?',
                icon: 'fa-regular fa-triangle-exclamation',
                acceptLabel: 'Ersetzen',
                rejectLabel: 'Abbrechen',
                accept: () => this.callUploadExtensionSchema()
            });
            return;
        }

        if (hasFile) {
            this.callUploadExtensionSchema();
            return;
        }

        this.callEnsureSchema();
    }

    /**
     * Calls {@link HgPartnerService.ensureSchema}. Idempotent — if the partner
     * already has a schema the call simply returns it unchanged.
     *
     * @ticket TM-1000001721
     */
    private callEnsureSchema(): void {
        this.busy = true;
        const hadSchemaBefore = !!this.partner.schemaDtoId;

        this.partnerService.ensureSchema(
            this.partner.id,
            this.schemaTypeId,
            this.includeNonDataMessageTypes
        ).pipe(takeUntil(this.destroy$)).subscribe({
            next: (updated: HgPartnerDto) => {
                this.busy = false;
                if (hadSchemaBefore && updated?.schemaDtoId === this.partner.schemaDtoId) {
                    this.messageService.showInfoMessage(
                        'Schema unverändert',
                        'Partner hat bereits ein Schema — keine Änderung.'
                    );
                } else {
                    this.messageService.showSuccessMessage(
                        'Schema eingerichtet',
                        `Schema erfolgreich eingerichtet (ID: ${updated?.schemaDtoId}).`
                    );
                }
                this.onSchemaUpdated.emit(updated);
                this.close();
            },
            error: (err) => {
                this.busy = false;
                this.messageService.showErrorMessage(
                    'Schema-Einrichtung fehlgeschlagen',
                    this.extractErrorMessage(err)
                );
            }
        });
    }

    /**
     * Calls {@link HgPartnerService.uploadExtensionSchema}. Replaces the
     * partner's current schema with the merged result.
     *
     * @ticket TM-1000001721
     */
    private callUploadExtensionSchema(): void {
        if (!this.selectedFile) {
            return;
        }
        this.busy = true;
        const orgName = this.organizationName?.trim() || undefined;

        this.partnerService.uploadExtensionSchema(
            this.partner.id,
            this.schemaTypeId,
            this.selectedFile,
            orgName
        ).pipe(takeUntil(this.destroy$)).subscribe({
            next: (updated: HgPartnerDto) => {
                this.busy = false;
                this.messageService.showSuccessMessage(
                    'Schema eingerichtet',
                    `Schema erfolgreich eingerichtet (ID: ${updated?.schemaDtoId}).`
                );
                this.onSchemaUpdated.emit(updated);
                this.close();
            },
            error: (err) => {
                this.busy = false;
                this.messageService.showErrorMessage(
                    'Schema-Einrichtung fehlgeschlagen',
                    this.extractErrorMessage(err)
                );
            }
        });
    }

    /**
     * Extracts a user-friendly error message from an HTTP error response.
     *
     * @ticket TM-1000001721
     */
    private extractErrorMessage(err: any): string {
        return err?.error?.message
            || err?.error?.error
            || err?.message
            || 'Ein unbekannter Fehler ist aufgetreten.';
    }

    /**
     * Cancels the dialog without performing any backend call.
     *
     * @ticket TM-1000001721
     */
    cancel(): void {
        this.close();
    }

    /**
     * Hides the dialog and notifies the parent via two-way binding.
     *
     * @ticket TM-1000001721
     */
    private close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    /**
     * Convenience for the template: whether the submit button should be enabled.
     *
     * @ticket TM-1000001721
     */
    get canSubmit(): boolean {
        return !this.busy && !!this.schemaTypeId;
    }
}
